import React from 'react';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { FileUploader, Chart, FileName } from '@usacsmideo/types';
import { getFileUploadPath } from '../data/getFileUploadPath';
import { useAuth } from '../contexts/AuthContext';
import useChart from '../hooks/useChart';
import { patientStorage, videoStorage, publicStorage } from '../constants/firebaseModular';
import { useHandleFileUploadSuccess } from '../hooks/useHandleFileUploadSuccess';
import * as Sentry from '@sentry/browser';

export const Uploader = (props: FileUploader) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [progress, setProgress] = React.useState(0);
  const [isError, setIsError] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isUploaded, setIsUploaded] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const { currentUser } = useAuth();
  const chartQuery = useChart(currentUser ? currentUser.uid : '');
  const data = chartQuery.data as Chart;
  const { updateFirestore } = useHandleFileUploadSuccess();

  React.useEffect(() => {
    if (!data || !props.docToUpdate) return;
    const docData = data[props.docToUpdate];
    const currentDocKeys = docData && typeof docData === 'object' ? Object.keys(docData) : [];
    const currentFileName = FileName[props.type];
    if (currentDocKeys && currentDocKeys.includes(currentFileName) && isUploaded) {
      setIsSuccess(true);
      return;
    }
  }, [data, props, isUploaded]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  // SELECT THE STORAGE BUCKET
  let storage;
  if (props.bucket === 'patient') {
    storage = patientStorage;
  }
  if (props.bucket === 'video') {
    storage = videoStorage;
  }
  if (props.bucket === 'public') {
    storage = publicStorage;
  }

  //FIRE THE UPLOAD REQUEST
  if (!isUploading && storage && file) {
    setIsUploading(true);
    const path = getFileUploadPath(props.type);
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);
    // MONITOR THE UPLOAD REQUEST
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        setProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        switch (snapshot.state) {
          case 'paused':
            console.warn('Upload is paused');
            break;
        }
      },
      (error) => {
        alert(`Error uploading file: ${error.message}`); // TELL THE USER
        console.error('Error uploading file:', error); // GET THE STACKTRACE
        setIsError(true);
      },
      () => {
        // UPLOAD SUCCESSFUL
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            // SAVE THE DOWNLOAD URL TO FIRESTORE
            if (downloadURL) {
              setIsUploaded(true);
              setIsSuccess(true);
              const payload = {
                docToUpdate: props?.docToUpdate,
                patientId: currentUser?.uid || ' ',
                url: downloadURL,
                type: props.type,
              };
              updateFirestore(payload);
            }
            setIsUploading(false);
          })
          .catch((error) => {
            Sentry.captureException(error);
            console.error('Error getting download URL:', error);
            setIsError(true);
          });
      },
    );
  }

  return (
    <>
      <div className="flex flex-row items-center">
        <label htmlFor="file" className="mr-4 text-2xl font-bold">
          {isSuccess && <span>✅ </span>}
          {isError && <span>❌ </span>}
          {/* {props.title ? props.title : ''} */}
        </label>
        <input
          id={props.elementId}
          type="file"
          onChange={handleFileChange}
          className="block w-full rounded-lg border border-gray-200 text-sm shadow-sm file:me-4 file:border-0 file:bg-gray-50 file:px-4 file:py-3 focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:file:bg-neutral-700 dark:file:text-neutral-400"
        />
        {progress > 0 && progress < 100 && (
          <progress value={progress} max={100}>
            {progress}%
          </progress>
        )}
      </div>
    </>
  );
};
